import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ExternalLink, AlertTriangle, Server, Zap, Box, Layers, Eye, FileText, RefreshCw } from 'lucide-react';
import { registryApi } from '../../services/registryService';
import TierBadge from '../../components/registry/TierBadge';
import StatusIndicator from '../../components/registry/StatusIndicator';
import BlastRadiusModal from '../../components/registry/BlastRadiusModal';

const COMPONENT_ICONS = {
  api: Server,
  lambda: Zap,
  'batch-job': Box,
  'queue-listener': Layers,
  'web-app': Eye,
  'mobile-app': Eye,
  'glue-job': Box,
  'dbt-project': Layers,
  'step-function': Layers,
  'static-site': FileText,
};

export default function ServiceDetail({ serviceId, onNavigate }) {
  const [service, setService] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBlastRadius, setShowBlastRadius] = useState(false);
  const [activeSection, setActiveSection] = useState('metadata');
  const [runbook, setRunbook] = useState(null);
  const [runbookLoading, setRunbookLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const result = await registryApi.getService(serviceId);
        setService(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [serviceId]);

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const result = await registryApi.getServiceHealth(serviceId);
      setHealth(result.data);
    } catch (err) {
      console.warn('Health fetch failed:', err.message);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    if (service) fetchHealth();
  }, [service]);

  useEffect(() => {
    setRunbookLoading(true);
    registryApi.getRunbook(serviceId)
      .then((result) => setRunbook(result.data))
      .catch(() => setRunbook({ content: null }))
      .finally(() => setRunbookLoading(false));
  }, [serviceId]);

  // Extract Quick Reference section from runbook for the Overview tab
  const quickRef = React.useMemo(() => {
    if (!runbook?.content) return null;
    const match = runbook.content.match(/## Quick Reference[^\n]*\n([\s\S]*?)(?=\n---|\n## (?!Quick))/);
    return match ? match[1].trim() : null;
  }, [runbook]);

  // Runbook content without the Quick Reference section (for Runbook tab)
  const runbookBody = React.useMemo(() => {
    if (!runbook?.content) return null;
    return runbook.content.replace(/## Quick Reference[^\n]*\n[\s\S]*?(?=\n---|\n## (?!Quick))/, '').replace(/^---\n/, '');
  }, [runbook]);

  const SECTIONS = [
    { id: 'metadata', label: 'Overview' },
    { id: 'components', label: `Components (${service?.component_count || 0})` },
    { id: 'dependencies', label: `Dependencies (${service?.dependency_count || 0})` },
    { id: 'observability', label: 'Observability' },
    { id: 'runbook', label: 'Runbook' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <button onClick={() => onNavigate('registry')} className="flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div>
      {/* Back + Header */}
      <button
        onClick={() => onNavigate('registry')}
        className="flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to catalog
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-bold text-gray-900">{service.service_name}</h2>
            <TierBadge tier={service.tier} />
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              service.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
              service.status === 'deprecated' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
            }`}>{service.status}</span>
          </div>
          <p className="text-sm text-gray-600">{service.capability}</p>
        </div>
        {health && (
          <div className="flex items-center gap-2">
            <StatusIndicator status={health.overall_status} showLabel pulse />
            <button onClick={fetchHealth} disabled={healthLoading} className="p-1.5 rounded-lg hover:bg-gray-100">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${healthLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeSection === s.id
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Metadata */}
      {activeSection === 'metadata' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Description</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
          </div>

          {quickRef && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
              <h3 className="text-sm font-semibold text-amber-800 mb-3">Quick Reference (On-Call / Management)</h3>
              <div className="prose prose-sm max-w-none prose-td:text-xs prose-th:text-xs prose-td:py-1.5 prose-td:px-2 prose-strong:text-gray-900 prose-li:text-xs prose-li:text-gray-700 prose-p:text-xs prose-p:text-gray-700 [&_table]:border-collapse [&_td]:border [&_td]:border-amber-200 [&_th]:border [&_th]:border-amber-200 [&_tr:first-child_td:first-child]:font-semibold [&_tr:first-child_td:first-child]:bg-amber-100/50 [&_td:first-child]:bg-amber-100/30 [&_td:first-child]:whitespace-nowrap">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{quickRef}</ReactMarkdown>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Ownership</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Team</span>
                  <span className="text-gray-800">{service.ownership?.team || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Teams</span>
                  <span className="text-gray-800">{service.ownership?.teams_channel || '—'}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Repositories</h3>
              <div className="space-y-2">
                {service.repositories?.map((repo) => (
                  <a
                    key={repo.repo_name}
                    href={repo.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-amber-700 hover:text-amber-800"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {repo.repo_name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {service.client_scope && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Client Scope</h3>
              <p className="text-sm text-gray-600">{service.client_scope}</p>
            </div>
          )}
        </div>
      )}

      {/* Components */}
      {activeSection === 'components' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 hidden md:table-cell">Runtime</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 hidden lg:table-cell">Path</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {service.components?.map((comp) => {
                const Icon = COMPONENT_ICONS[comp.type] || Server;
                const compHealth = health?.components?.find((c) => c.name === comp.name);
                return (
                  <tr key={comp.component_id}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-700">
                        <Icon className="w-3.5 h-3.5" /> {comp.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{comp.name}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{comp.runtime_identifier || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell font-mono">{comp.path || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {compHealth ? (
                        <StatusIndicator status={compHealth.status} showLabel />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dependencies */}
      {activeSection === 'dependencies' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setShowBlastRadius(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
            >
              <AlertTriangle className="w-4 h-4" /> View Blast Radius
            </button>
          </div>

          {/* Outbound internal */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Depends On ({service.dependencies?.internal?.length || 0} services)
            </h3>
            {service.dependencies?.internal?.length > 0 ? (
              <div className="space-y-2">
                {service.dependencies.internal.map((dep) => (
                  <div
                    key={dep.target_service_id}
                    className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-amber-50"
                    onClick={() => onNavigate(`registry-detail:${dep.target_service_id}`)}
                  >
                    <TierBadge tier={dep.target_tier} />
                    <span className="text-sm font-medium text-gray-800">{dep.target_name}</span>
                    <span className="text-xs text-gray-400 ml-auto">{dep.protocol}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No internal dependencies.</p>
            )}
          </div>

          {/* Outbound external */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              External Dependencies ({service.dependencies?.external?.length || 0})
            </h3>
            {service.dependencies?.external?.length > 0 ? (
              <div className="space-y-2">
                {service.dependencies.external.map((dep, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                      {dep.external_type}
                    </span>
                    <span className="text-sm text-gray-800">{dep.external_name}</span>
                    {dep.detail && <span className="text-xs text-gray-400 ml-auto truncate max-w-[200px]">{dep.detail}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No external dependencies.</p>
            )}
          </div>

          {/* Reverse */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Used By ({service.reverse_dependencies?.length || 0} services)
            </h3>
            {service.reverse_dependencies?.length > 0 ? (
              <div className="space-y-2">
                {service.reverse_dependencies.map((dep) => (
                  <div
                    key={dep.source_service_id}
                    className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-amber-50"
                    onClick={() => onNavigate(`registry-detail:${dep.source_service_id}`)}
                  >
                    <TierBadge tier={dep.source_tier} />
                    <span className="text-sm font-medium text-gray-800">{dep.source_name}</span>
                    <span className="text-xs text-gray-400 ml-auto">{dep.protocol}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No services depend on this one.</p>
            )}
          </div>
        </div>
      )}

      {/* Observability */}
      {activeSection === 'observability' && (
        <div className="space-y-4">
          {['dashboard', 'log_group', 'alarm'].map((type) => {
            const bindings = service.observability?.filter((b) => b.binding_type === type) || [];
            const label = type === 'log_group' ? 'Log Groups' : type === 'dashboard' ? 'Dashboards' : 'Alarms';
            return (
              <div key={type} className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{label} ({bindings.length})</h3>
                {bindings.length > 0 ? (
                  <div className="space-y-2">
                    {bindings.map((b) => (
                      <div key={b.binding_id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{b.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{b.value}</p>
                        </div>
                        {b.environment && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">{b.environment}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No {label.toLowerCase()} configured.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Runbook */}
      {activeSection === 'runbook' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          {runbookLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : runbookBody ? (
            <div>
              {runbook.last_modified && (
                <p className="text-xs text-gray-400 mb-4">
                  Last updated: {new Date(runbook.last_modified).toLocaleDateString()}
                </p>
              )}
              <div className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-h1:text-lg prose-h1:border-b prose-h1:border-gray-200 prose-h1:pb-2 prose-h2:text-base prose-h3:text-sm prose-td:text-xs prose-th:text-xs prose-code:text-xs prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 [&_pre_code]:bg-transparent [&_pre_code]:text-gray-100 [&_pre_code]:p-0 prose-a:text-amber-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{runbookBody}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No runbook available for this service.</p>
              <p className="text-xs text-gray-400 mt-1">
                Create one at: <code className="bg-gray-100 px-1.5 py-0.5 rounded">runbooks/{service.service_id}.md</code>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Blast radius modal */}
      {showBlastRadius && (
        <BlastRadiusModal
          serviceId={service.service_id}
          serviceName={service.service_name}
          onClose={() => setShowBlastRadius(false)}
        />
      )}
    </div>
  );
}
